import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection,
  updateDoc,
  doc,
  serverTimestamp,
  getDoc,
  setDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import type { AppDispatch } from "./store";

// ---------------------- //
// TYPES
// ---------------------- //
export interface ShortReservation {
  id: string;
  guestId: string;
  hostId: string;
  listingId: string;
  checkIn: string;
  checkOut: string;
  reservedDates: number;
  totalPaid: number;
  extras: Record<string, any>;
  extraCharges: number;
  guestsFee: number;
  hostsFee: number;
  hostProfit: number;
  platformProfit: number;
  paymentId: string;

  // base flags
  isConfirmed: boolean;
  isCancelled: boolean;
  isRefunded: boolean;
  isPaid: boolean;
  hasCheckedIn: boolean;

  // derived flags
  isUpcoming: boolean;
  isOngoing: boolean;
  isHosted: boolean;
  isCompleted: boolean;
  isCheckingIn: boolean;
  isCheckingOut: boolean;
  isArrivingSoon: boolean;

  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
}

export interface GuestCredit {
  guestId: string;
  credits: number;
  lastCreditedAt?: any;
  lastUpdated?: any;
  refunded?: boolean;
  refundedAt?: any;
}

// ✅ UPDATED INTERFACE ONLY
export interface ShortstayRequest {
  requestId: string;
  listingId: string;
  guestId: string;
  hostId: string;
  checkIn: string;
  checkOut: string;
  isPending: boolean;
  isApproved: boolean;
  isRejected: boolean;
  createdAt?: any;
  updatedAt?: any;
  adults?: number;
  children?: number;
  infants?: number;
  pets?: number;
  selectedExtras?: string[];
  selectedTimes?: string[];
  totalHours?: number;
  reservedDuration?: number;
  rateType?: string;
}

// ---------------------- //
// HELPERS
// ---------------------- //
export const calculateStatusFlags = (checkIn: string, checkOut: string) => {
  const now = new Date();
  const today = new Date(now.toDateString());

  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);

  checkInDate.setHours(0, 0, 0, 0);
  checkOutDate.setHours(0, 0, 0, 0);

  const diffToCheckIn =
    (checkInDate.getTime() - today.getTime()) / (1000 * 3600 * 24);

  const diffFromCheckOut =
    (today.getTime() - checkOutDate.getTime()) / (1000 * 3600 * 24);

  const isCompleted = diffFromCheckOut > 7;
  const isCheckingOut = diffFromCheckOut >= 0 && diffFromCheckOut <= 7;

  return {
    isUpcoming: diffToCheckIn > 3,
    isArrivingSoon: diffToCheckIn <= 3 && diffToCheckIn > 0,
    isCheckingIn: diffToCheckIn === 0,
    isOngoing: today >= checkInDate && today < checkOutDate,
    isCheckingOut,
    isCompleted,
    isHosted: isCompleted,
  };
};

// ---------------------- //
// UPSERT RESERVATION
// ---------------------- //
export const upsertShortstayReservation = createAsyncThunk<
  ShortReservation,
  Partial<ShortReservation> & { paymentId: string },
  { rejectValue: string }
>("shortstayReservations/upsert", async (reservationData, { rejectWithValue }) => {
  try {
    if (!reservationData.paymentId) throw new Error("Missing paymentId");

    const reservationId = reservationData.paymentId;
    const docRef = doc(db, "shortstayReservations", reservationId);

    const flags = calculateStatusFlags(
      reservationData.checkIn!,
      reservationData.checkOut!
    );

    const existingSnap = await getDoc(docRef);

    if (existingSnap.exists()) {
      await updateDoc(docRef, {
        ...reservationData,
        ...flags,
        updatedAt: serverTimestamp(),
      });

      const existing = existingSnap.data() as ShortReservation;
      const { id: _ignored, ...restExisting } = existing;
      const { id: _ignored2, ...restData } = reservationData;

      return { id: reservationId, ...restExisting, ...restData, ...flags } as ShortReservation;
    } else {
      const payload: Omit<ShortReservation, "id"> = {
        guestId:        reservationData.guestId!,
        hostId:         reservationData.hostId!,
        listingId:      reservationData.listingId!,
        checkIn:        reservationData.checkIn!,
        checkOut:       reservationData.checkOut!,
        reservedDates:  reservationData.reservedDates  || 0,
        totalPaid:      reservationData.totalPaid      || 0,
        extras:         reservationData.extras         || {},
        extraCharges:   reservationData.extraCharges   || 0,
        guestsFee:      reservationData.guestsFee      || 0,
        hostsFee:       reservationData.hostsFee       || 0,
        hostProfit:     reservationData.hostProfit     || 0,
        platformProfit: reservationData.platformProfit || 0,
        paymentId:      reservationData.paymentId,
        isConfirmed:    true,
        isCancelled:    false,
        isRefunded:     false,
        isPaid:         false,
        hasCheckedIn:   false,
        ...flags,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(docRef, payload, { merge: true });
      return { id: reservationId, ...payload } as ShortReservation;
    }
  } catch (err: any) {
    console.error("🔥 upsertShortstayReservation error:", err);
    return rejectWithValue(err.message || "Failed to upsert shortstay reservation");
  }
});

// ---------------------- //
// CHECK DATE AVAILABILITY
// ---------------------- //
export const checkShortstayDateAvailability = createAsyncThunk<
  { available: boolean; conflict?: ShortReservation },
  { listingId: string; checkIn: string; checkOut: string },
  { rejectValue: string }
>(
  "shortstayReservations/checkAvailability",
  async ({ listingId, checkIn, checkOut }, { rejectWithValue }) => {
    try {
      const q = query(
        collection(db, "shortstayReservations"),
        where("listingId", "==", listingId)
      );
      const snapshot = await getDocs(q);

      const selectedStart = new Date(checkIn).getTime();
      const selectedEnd   = new Date(checkOut).getTime();

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data() as ShortReservation;

        const existingStart = new Date(data.checkIn).getTime();
        const existingEnd   = new Date(data.checkOut).getTime();

        const isActive =
          data.isConfirmed === true &&
          data.isCancelled !== true &&
          data.isRefunded  !== true;

        if (isActive && selectedStart < existingEnd && selectedEnd > existingStart) {
          return { available: false, conflict: { ...data, id: docSnap.id } };
        }
      }

      return { available: true };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ---------------------- //
// REAL-TIME HOST LISTENER
// ---------------------- //
export const listenToHostReservations =
  (hostId: string) => (dispatch: AppDispatch) => {
    if (!hostId) return;

    const q = query(
      collection(db, "shortstayReservations"),
      where("hostId", "==", hostId)
    );

    return onSnapshot(q, (snapshot) => {
      const reservations: ShortReservation[] = snapshot.docs.map((docSnap) => {
        const data  = docSnap.data() as Omit<ShortReservation, "id">;
        const flags = calculateStatusFlags(data.checkIn, data.checkOut);
        return { ...data, id: docSnap.id, ...flags } as ShortReservation;
      });
      dispatch(setHostReservations(reservations));
    });
  };

// ---------------------- //
// REAL-TIME GUEST LISTENER
// ---------------------- //
export const listenToShortGuestReservations =
  (guestId: string) => (dispatch: AppDispatch) => {
    if (!guestId) return;

    const q = query(
      collection(db, "shortstayReservations"),
      where("guestId", "==", guestId)
    );

    return onSnapshot(q, (snapshot) => {
      const reservations: ShortReservation[] = snapshot.docs.map((docSnap) => {
        const data  = docSnap.data() as Omit<ShortReservation, "id">;
        const flags = calculateStatusFlags(data.checkIn, data.checkOut);
        return { ...data, id: docSnap.id, ...flags } as ShortReservation;
      });
      dispatch(setGuestReservations(reservations));
    });
  };

// ---------------------- //
// REAL-TIME SINGLE RESERVATION LISTENER
// ---------------------- //
export const listenToShortstayReservaionData =
  (reservationId: string) => (dispatch: AppDispatch) => {
    if (!reservationId) return;

    const ref = doc(db, "shortstayReservations", reservationId);

    return onSnapshot(ref, (docSnap) => {
      if (docSnap.exists()) {
        const data  = docSnap.data() as Omit<ShortReservation, "id">;
        const flags = calculateStatusFlags(data.checkIn, data.checkOut);
        dispatch(setReservationById({ ...data, id: docSnap.id, ...flags } as ShortReservation));
      } else {
        dispatch(setReservationById(null));
      }
    });
  };

// ---------------------- //
// RESCHEDULE
// ---------------------- //
export const rescheduleShortstayReservation = createAsyncThunk<
  ShortReservation,
  { reservationId: string; newCheckIn: string; newCheckOut: string },
  { rejectValue: string }
>(
  "shortstayReservations/reschedule",
  async ({ reservationId, newCheckIn, newCheckOut }, { rejectWithValue }) => {
    try {
      const ref  = doc(db, "shortstayReservations", reservationId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Reservation not found");

      const existing = snap.data() as ShortReservation;

      const reservedDates = Math.ceil(
        (new Date(newCheckOut).getTime() - new Date(newCheckIn).getTime()) /
        (1000 * 60 * 60 * 24)
      );

      const flags = calculateStatusFlags(newCheckIn, newCheckOut);

      await updateDoc(ref, {
        checkIn: newCheckIn,
        checkOut: newCheckOut,
        reservedDates,
        ...flags,
        updatedAt: serverTimestamp(),
      });

      return {
        ...existing,
        id: reservationId,
        checkIn: newCheckIn,
        checkOut: newCheckOut,
        reservedDates,
        ...flags,
      } as ShortReservation;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ---------------------- //
// REAL-TIME LISTENER (ALL RESERVATIONS + HOST BLOCKS)
// ---------------------- //
export const listenToAllShortstayReservations =
  (checkIn?: string, checkOut?: string) =>
  (dispatch: AppDispatch) => {
    const reservationRef = collection(db, "shortstayReservations");
    const shortstayRef   = collection(db, "shortstays");

    const hasDates = Boolean(checkIn && checkOut);

    // Normalize to midnight UTC timestamps for comparison
    const toMidnightUTC = (dateStr: string): number => {
      // handles both "2026-05-10" and "2026-06-01T00:00:00Z"
      const plain = dateStr.split("T")[0]; // strip time part
      return Date.parse(`${plain}T00:00:00Z`);
    };

    const selectedStart = hasDates ? toMidnightUTC(checkIn!) : null;
    const selectedEnd   = hasDates ? toMidnightUTC(checkOut!) : null;
    const today = toMidnightUTC(new Date().toISOString());

    // Track blocked IDs across both listeners
    const reservationBlocked = new Set<string>();
    const unavailableBlocked = new Set<string>();

    const dispatchMerged = () => {
      const merged = new Set(
        Array.from(reservationBlocked).concat(Array.from(unavailableBlocked))
      );
      dispatch(setBlockedListingIds(Array.from(merged)));
    };

    const unsubReservations = onSnapshot(reservationRef, (snapshot) => {
      const reservations: ShortReservation[] = [];
      reservationBlocked.clear();

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() as ShortReservation;
        const reservation: ShortReservation = {
          ...data,
          id:          docSnap.id,
          isConfirmed: data.isConfirmed ?? false,
          isCancelled: data.isCancelled ?? false,
          isRefunded:  data.isRefunded  ?? false,
        };
        reservations.push(reservation);

        if (!hasDates) return;

        const isActive =
          reservation.isConfirmed &&
          !reservation.isCancelled &&
          !reservation.isRefunded;

        if (!isActive) return;

        // Normalize both checkIn/checkOut from reservation (ISO or plain)
        const existingStart = toMidnightUTC(reservation.checkIn);
        const existingEnd   = toMidnightUTC(reservation.checkOut);

        if (isNaN(existingStart) || isNaN(existingEnd)) return;
        if (existingEnd <= today) return;

        if (selectedStart! < existingEnd && selectedEnd! > existingStart) {
          reservationBlocked.add(reservation.listingId);
        }
      });

      dispatch(setAllReservations(reservations));
      dispatchMerged();
    });

    const unsubShortstays = onSnapshot(shortstayRef, (snapshot) => {
      if (!hasDates) return;
      unavailableBlocked.clear();

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const listingId: string = data.listingId ?? docSnap.id;
        const unavailableDates: string[] = data.unavailableDates || [];

        for (const date of unavailableDates) {
          // These are always plain "YYYY-MM-DD" strings
          const blockedDate = toMidnightUTC(date);
          if (isNaN(blockedDate)) continue;
          if (blockedDate < today) continue;

          // Block if ANY unavailable date falls within selected range
          if (selectedStart! <= blockedDate && blockedDate < selectedEnd!) {
            unavailableBlocked.add(listingId);
            break;
          }
        }
      });

      dispatchMerged();
    });

    return () => {
      unsubReservations();
      unsubShortstays();
    };
  };

// ---------------------- //
// REAL-TIME LISTENER BY LISTING
// ---------------------- //
export const listenToListingReservations =
  (listingId: string) => (dispatch: AppDispatch) => {
    if (!listingId) return;

    const q = query(
      collection(db, "shortstayReservations"),
      where("listingId", "==", listingId)
    );

    return onSnapshot(q, (snapshot) => {
      const reservations = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();
          return {
            id:          docSnap.id,
            ...data,
            isConfirmed: data?.isConfirmed ?? false,
            isCancelled: data?.isCancelled ?? false,
            isRefunded:  data?.isRefunded  ?? false,
          } as ShortReservation;
        })
        .filter((r) => r.isConfirmed && !r.isCancelled);

      dispatch(setListingReservations(reservations));
    });
  };

// ---------------------- //
// GUEST CREDITS LISTENER
// ---------------------- //
export const listenToGuestCredit =
  (guestId: string) => (dispatch: AppDispatch) => {
    if (!guestId) return;

    const ref = doc(db, "shortCredits", guestId);

    return onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data    = snap.data();
        const credits = data.credits || 0;
        dispatch(setGuestCredit({ guestId, credits }));
      } else {
        dispatch(setGuestCredit({ guestId, credits: 0 }));
      }
    });
  };

// ---------------------- //
// UPSERT GUEST LIST
// ---------------------- //
export const upsertShortGuestList = createAsyncThunk<
  void,
  {
    reservationId: string;
    userId: string;
    listingId: string;
    guests: {
      fullName: string;
      contactNumber?: string;
      emailAddress?: string;
      passportId?: string;
      country?: string;
      dateOfBirth?: string;
      verified?: boolean;
      isPrimary?: boolean;
    }[];
  },
  { rejectValue: string }
>(
  "shortstayReservations/upsertShortGuestList",
  async ({ reservationId, userId, listingId, guests }, { rejectWithValue }) => {
    try {
      if (!reservationId || !userId) throw new Error("Missing reservation or user ID");

      const mainRef = doc(db, "shortGuestList", reservationId);

      await setDoc(
        mainRef,
        { reservationId, userId, listingId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
        { merge: true }
      );

      const guestListRef = collection(mainRef, "guestList");

      for (const guest of guests) {
        const guestDocRef = doc(guestListRef);
        await setDoc(
          guestDocRef,
          { ...guest, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
          { merge: true }
        );
      }

      const reservationRef = doc(db, "shortstayReservations", reservationId);
      await updateDoc(reservationRef, { hasGuestList: true, updatedAt: serverTimestamp() });
    } catch (err: any) {
      console.error("🔥 upsertShortGuestList error:", err);
      return rejectWithValue(err.message || "Failed to save guest list");
    }
  }
);

// ---------------------- //
// LISTING AVAILABILITY LISTENER
// ---------------------- //
export const listenToListingAvailability =
  (listingId: string, checkIn: string, checkOut: string) =>
  (dispatch: AppDispatch) => {
    if (!listingId || !checkIn || !checkOut) return;

    const toMidnightUTC = (dateStr: string): number =>
      Date.parse(`${dateStr.split("T")[0]}T00:00:00Z`);

    const selectedStart = toMidnightUTC(checkIn);
    const selectedEnd   = toMidnightUTC(checkOut);
    const today         = toMidnightUTC(new Date().toISOString());

    let reservationConflict: ShortReservation | null = null;
    let unavailableConflict = false;

    const dispatchResult = () => {
      const conflict = reservationConflict;
      dispatch(setListingAvailability({
        available: !reservationConflict && !unavailableConflict,
        conflict,
      }));
    };

    // 1️⃣ Listen to reservations for this listing
    const reservationsQuery = query(
      collection(db, "shortstayReservations"),
      where("listingId", "==", listingId)
    );

    const unsubReservations = onSnapshot(reservationsQuery, (snapshot) => {
      reservationConflict = null;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data() as ShortReservation;

        const isActive =
          data.isConfirmed &&
          !data.isCancelled &&
          !data.isRefunded;

        if (!isActive) continue;

        const existingStart = toMidnightUTC(data.checkIn);
        const existingEnd   = toMidnightUTC(data.checkOut);

        if (isNaN(existingStart) || isNaN(existingEnd)) continue;
        if (existingEnd <= today) continue;

        if (selectedStart < existingEnd && selectedEnd > existingStart) {
          reservationConflict = { ...data, id: docSnap.id };
          break;
        }
      }

      dispatchResult();
    });

    // 2️⃣ Listen to listing's own unavailableDates
    const listingRef = doc(db, "shortstays", listingId);

    const unsubListing = onSnapshot(listingRef, (snap) => {
      if (!snap.exists()) return;

      const data = snap.data() as any;
      const unavailableDates: string[] = data.unavailableDates || [];

      unavailableConflict = unavailableDates.some((d: string) => {
        const blocked = toMidnightUTC(d);
        if (isNaN(blocked) || blocked < today) return false;
        return selectedStart <= blocked && blocked < selectedEnd;
      });

      dispatchResult();
    });

    return () => {
      unsubReservations();
      unsubListing();
    };
  };





export const listenToListingAvailabilityByHours =
  (payload: { listingId: string; date: string }) =>
  (dispatch: AppDispatch) => {
    const { listingId, date } = payload;
    if (!listingId || !date) return;

    const plainDate = date.split("T")[0];
    const listingRef = doc(db, "shortstays", listingId);

    const reservationsRef = query(
      collection(db, "shortstayReservations"),
      where("listingId", "==", listingId),
      where("isCancelled", "==", false)
    );

    let hostHours: number[] = [];
    let reservedHours: number[] = [];

    const recompute = () => {
      const merged = Array.from(new Set([...hostHours, ...reservedHours])).sort(
        (a, b) => a - b
      );

      dispatch(setHourAvailability({
        listingId,
        date: plainDate,
        disabledHours: merged,
        isFullyBlocked: merged.length === 24,
        isPartiallyBlocked: merged.length > 0 && merged.length < 24,
      }));
    };

    const unsubListing = onSnapshot(listingRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as any;
      const unavailableHours = data.unavailableHours || [];
      const entry = unavailableHours.find(
        (h: any) => h.date?.split("T")[0] === plainDate
      );
      hostHours = entry?.hours || [];
      recompute();
    });

    const unsubReservations = onSnapshot(reservationsRef, (snap) => {
      const hours: number[] = [];
      snap.forEach((docSnap) => {
        const r = docSnap.data() as any;
        if (!r.isConfirmed || r.isCancelled || r.isRefunded) return;
        const reservationDate = r.checkIn?.split("T")[0];
        if (reservationDate !== plainDate) return;
        if (r.rateType?.includes("perHour") && Array.isArray(r.reservedHours)) {
          r.reservedHours.forEach((t: string) => {
            const h = Number(t.split(":")[0]);
            if (!Number.isNaN(h)) hours.push(h);
          });
        }
      });
      reservedHours = hours;
      recompute();
    });

    return () => {
      unsubListing();
      unsubReservations();
    };
  };








// ---------------------- //
// UPSERT SHORTSTAY REQUEST  ✅ UPDATED THUNK ONLY
// ---------------------- //
export const upsertShortstayRequest = createAsyncThunk<
  ShortstayRequest,
  Omit<ShortstayRequest, "requestId" | "isPending" | "isApproved" | "isRejected" | "createdAt" | "updatedAt">,
  { rejectValue: string }
>(
  "shortstayRequests/upsert",
  async (requestData, { rejectWithValue }) => {
    try {
      const docRef    = doc(collection(db, "ShortstayRequests"));
      const requestId = docRef.id;

      const payload: Omit<ShortstayRequest, "requestId"> = {
        listingId:        requestData.listingId,
        guestId:          requestData.guestId,
        hostId:           requestData.hostId,
        checkIn:          requestData.checkIn,
        checkOut:         requestData.checkOut,
        isPending:        true,
        isApproved:       false,
        isRejected:       false,
        adults:           requestData.adults          ?? 1,
        children:         requestData.children        ?? 0,
        infants:          requestData.infants         ?? 0,
        pets:             requestData.pets            ?? 0,
        selectedExtras:   requestData.selectedExtras  ?? [],
        selectedTimes:    requestData.selectedTimes   ?? [],
        totalHours:       requestData.totalHours      ?? 0,
        reservedDuration: requestData.reservedDuration ?? 0,
        rateType:         requestData.rateType        ?? "",
        createdAt:        serverTimestamp(),
        updatedAt:        serverTimestamp(),
      };

      await setDoc(docRef, payload);
      return { requestId, ...payload } as ShortstayRequest;
    } catch (err: any) {
      console.error("🔥 upsertShortstayRequest error:", err);
      return rejectWithValue(err.message || "Failed to create request");
    }
  }
);

// ---------------------- //
// REAL-TIME HOST REQUEST LISTENER
// ---------------------- //
export const listenToHostReservationRequests =
  (hostId: string) => (dispatch: AppDispatch) => {
    if (!hostId) return;

    const q = query(
      collection(db, "ShortstayRequests"),
      where("hostId", "==", hostId)
    );

    return onSnapshot(q, (snapshot) => {
      const requests: ShortstayRequest[] = snapshot.docs.map((docSnap) => ({
        ...(docSnap.data() as Omit<ShortstayRequest, "requestId">),
        requestId: docSnap.id,
      }));
      dispatch(setHostReservationRequests(requests));
    });
  };

// ---------------------- //
// REAL-TIME LISTENER BY LISTING
// ---------------------- //
export const listenToListingReservationRequests =
  (listingId: string) => (dispatch: AppDispatch) => {
    if (!listingId) return;

    const q = query(
      collection(db, "ShortstayRequests"),
      where("listingId", "==", listingId)
    );

    return onSnapshot(q, (snapshot) => {
      const requests: ShortstayRequest[] = snapshot.docs.map((docSnap) => ({
        ...(docSnap.data() as Omit<ShortstayRequest, "requestId">),
        requestId: docSnap.id,
      }));
      dispatch(setListingReservationRequests(requests));
    });
  };

// ---------------------- //
// REAL-TIME GUEST REQUEST LISTENER
// ---------------------- //
export const listenToGuestReservationRequests =
  (guestId: string) => (dispatch: AppDispatch) => {
    if (!guestId) return;

    const q = query(
      collection(db, "ShortstayRequests"),
      where("guestId", "==", guestId)
    );

    return onSnapshot(q, (snapshot) => {
      const requests: ShortstayRequest[] = snapshot.docs.map((docSnap) => ({
        ...(docSnap.data() as Omit<ShortstayRequest, "requestId">),
        requestId: docSnap.id,
      }));
      dispatch(setGuestReservationRequests(requests));
    });
  };

// ---------------------- //
// SLICE
// ---------------------- //
const shortstayReservationSlice = createSlice({
  name: "shortstayReservations",
  initialState: {
    guestReservations:          [] as ShortReservation[],
    hostReservations:           [] as ShortReservation[],
    listingReservations:        [] as ShortReservation[],
    reservationById:            null as ShortReservation | null,
    allReservations:            [] as ShortReservation[],
    blockedListingIds:          [] as string[],
    guestCredit:                null as GuestCredit | null,
    listingAvailability: {
      available: true,
      conflict:  null as ShortReservation | null,
    },
    hourAvailability: null as {
  listingId?: string;
  date?: string;
  disabledHours: number[];
  isFullyBlocked: boolean;
  isPartiallyBlocked: boolean;
} | null,
    hostReservationRequests:    [] as ShortstayRequest[],
    guestReservationRequests:   [] as ShortstayRequest[],
    listingReservationRequests: [] as ShortstayRequest[],
    loading: false,
    error:   null as string | null,
  },
  reducers: {
    setGuestReservations: (state, action: PayloadAction<ShortReservation[]>) => {
      state.guestReservations = action.payload;
    },
    setHostReservations: (state, action: PayloadAction<ShortReservation[]>) => {
      state.hostReservations = action.payload;
    },
    setReservationById: (state, action: PayloadAction<ShortReservation | null>) => {
      state.reservationById = action.payload;
    },
    setAllReservations: (state, action: PayloadAction<ShortReservation[]>) => {
      state.allReservations = action.payload;
    },
    setBlockedListingIds: (state, action: PayloadAction<string[]>) => {
      state.blockedListingIds = action.payload;
    },
    setListingReservations: (state, action: PayloadAction<ShortReservation[]>) => {
      state.listingReservations = action.payload;
    },
    setGuestCredit: (state, action: PayloadAction<GuestCredit>) => {
      state.guestCredit = action.payload;
    },
    setListingAvailability: (
      state,
      action: PayloadAction<{ available: boolean; conflict: ShortReservation | null }>
    ) => {
      state.listingAvailability = action.payload;
    },
    setHostReservationRequests: (state, action: PayloadAction<ShortstayRequest[]>) => {
      state.hostReservationRequests = action.payload;
    },
    setGuestReservationRequests: (state, action: PayloadAction<ShortstayRequest[]>) => {
      state.guestReservationRequests = action.payload;
    },
    setListingReservationRequests: (state, action: PayloadAction<ShortstayRequest[]>) => {
      state.listingReservationRequests = action.payload;
    },
    // ADD to reducers in shortstayReservationSlice
setHourAvailability: (
  state,
  action: PayloadAction<{
    listingId: string;
    date: string;
    disabledHours: number[];
    isFullyBlocked: boolean;
    isPartiallyBlocked: boolean;
  }>
) => {
  state.hourAvailability = action.payload;
},
  },
  extraReducers: (builder) => {
    builder
      .addCase(upsertShortstayReservation.pending, (state) => {
        state.loading = true;
      })
      .addCase(upsertShortstayReservation.fulfilled, (state, action) => {
        const idx = state.guestReservations.findIndex((r) => r.id === action.payload.id);
        if (idx >= 0) {
          state.guestReservations[idx] = action.payload;
        } else {
          state.guestReservations.push(action.payload);
        }
        state.loading = false;
      })
      .addCase(upsertShortstayReservation.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || "Failed to upsert reservation";
      })
      .addCase(upsertShortstayRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(upsertShortstayRequest.fulfilled, (state, action) => {
        const idx = state.hostReservationRequests.findIndex(
          (r) => r.requestId === action.payload.requestId
        );
        if (idx >= 0) {
          state.hostReservationRequests[idx] = action.payload;
        } else {
          state.hostReservationRequests.push(action.payload);
        }
        state.loading = false;
      })
      .addCase(upsertShortstayRequest.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || "Failed to upsert request";
      });
  },
});

export const {
  setGuestReservations,
  setHostReservations,
  setReservationById,
  setAllReservations,
  setBlockedListingIds,
  setListingReservations,
  setGuestCredit,
  setListingAvailability,
  setHostReservationRequests,
  setGuestReservationRequests,
  setListingReservationRequests,
  setHourAvailability,
} = shortstayReservationSlice.actions;

export default shortstayReservationSlice.reducer;